import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

import coverImage from "@/assets/blog/push-marketing/couverture.webp";
import pushVsPullImage from "@/assets/blog/push-marketing/push-vs-pull-marketing-schema.webp";
import canauxPushImage from "@/assets/blog/push-marketing/canaux-push-2026-taux-ouverture-comparatif.webp";
import kpiPushImage from "@/assets/blog/push-marketing/kpi-strategie-push-marketing-dashboard.webp";
import walletEcranImage from "@/assets/blog/push-marketing/notification-push-wallet-ecran-verrouille.webp";

const PushMarketing = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Push marketing : définition, canaux et stratégie complète 2026",
      seoConfig.blogPushMarketing.description,
      "/push-marketing"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Push marketing", path: "/push-marketing" }
    ]),
    generateArticleSchema(
      "Push marketing : définition, canaux et stratégie complète 2026",
      seoConfig.blogPushMarketing.description,
      "https://unotif.com/push-marketing",
      "2026-04-28"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogPushMarketing} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Push marketing</span>
            </nav>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Push marketing : définition, canaux et stratégie complète 2026
            </h1>

            <img
              src={coverImage}
              alt="Stratégie de push marketing avec canaux comme email SMS notifications et wallet pour contacter les clients directement"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Introduction */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">push marketing</strong> désigne toute démarche par laquelle une entreprise initie le contact avec sa cible, sans attendre que celle-ci vienne chercher l'information. L'entreprise décide quand envoyer, quoi envoyer, et à qui. Le consommateur reçoit sans avoir cherché.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              C'est l'opposé du pull marketing, qui consiste à être présent au moment où le consommateur fait une recherche active. Ces deux logiques ne s'excluent pas : elles se séquencent. En 2026, le <strong className="font-semibold">push marketing</strong> recouvre un spectre de canaux très large : publicité display, SMS, email, notifications push mobile, et push wallet. Comprendre ces nuances est indispensable pour choisir le bon canal selon son objectif et l'étape du cycle de vie de son client. Pour positionner le push marketing dans une stratégie de fidélisation globale, notre guide sur <Link to="/fidelisation-client-guide" className="text-[#0c97a9] hover:underline">comment fidéliser ses clients</Link> pose les bases.
            </p>

            {/* Section 1 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Définition du push marketing : deux dimensions à distinguer
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le terme "push marketing" recouvre deux réalités distinctes que la plupart des articles confondent.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La première est <strong className="font-semibold">stratégique</strong> : le push marketing est une initiative unilatérale de l'entreprise vers le consommateur. L'entreprise choisit le moment, le message, le canal. Le consommateur n'a pas formulé de demande au moment de la réception. Cette logique s'oppose au pull marketing, où c'est l'intention du consommateur qui déclenche le contact.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La seconde est <strong className="font-semibold">technique</strong> : dans le vocabulaire des notifications mobiles, "push" désigne un message envoyé directement sur le terminal de l'utilisateur (smartphone, navigateur, montre connectée) sans qu'il en fasse la demande active. Cette acception est plus récente et vient du monde du développement mobile.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les deux dimensions se croisent : une notification push est un outil push au sens stratégique ET au sens technique. Mais un email promotionnel est du push marketing au sens stratégique sans être une notification push au sens technique. Tenir cette distinction permet de choisir son vocabulaire avec précision et, surtout, de choisir ses canaux avec cohérence.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              En 2026, le smartphone est le premier écran de contact en France : 75 % du temps de navigation digital passe par mobile. Cette donnée change la hiérarchie des canaux push. Ce qui arrive directement sur le téléphone a mécaniquement plus de chances d'être vu qu'un message qui attend dans une boîte mail.
            </p>

            <img
              src={pushVsPullImage}
              alt="Différence entre push marketing et pull marketing avec envoi de messages par l’entreprise et recherche par le consommateur"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Section 2 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les 5 canaux du push marketing en 2026
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les canaux push ne se valent pas tous. Leur efficacité dépend de trois variables : le taux d'attention capté au moment de la réception, le coût par message, et la friction à l'opt-in.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse rounded-xl overflow-hidden text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Canal</th>
                    <th className="px-4 py-3 text-left font-semibold">Taux d'ouverture moyen</th>
                    <th className="px-4 py-3 text-left font-semibold">Coût par envoi</th>
                    <th className="px-4 py-3 text-left font-semibold">Friction opt-in</th>
                    <th className="px-4 py-3 text-left font-semibold">Dépendance algorithme</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Email</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">23–30 %</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Très faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Non</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">SMS</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">90 %+</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Moyen à élevé</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Non</td>
                  </tr>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Notification push app</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">5–10 %</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Très faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Élevée (téléchargement)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Non</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Notification push navigateur</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">10–20 %</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Très faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Partielle</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Push wallet (Apple/Google Wallet)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">50–55 %</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Très faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Très faible (scan QR)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Non</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">L'email</strong> reste le canal push le plus utilisé. Il est adapté aux messages longs, aux séquences automatisées, et aux audiences habituées à ce canal. Sa limite : une boîte mail reçoit en moyenne 120 messages par jour. Le message est en compétition directe avec tout le reste.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Le SMS</strong> obtient les taux d'ouverture les plus élevés de tous les canaux marketing. Son coût par message et les règles RGPD strictes en limitent l'usage à des moments à forte valeur (confirmation de commande, offre flash limitée, rappel de rendez-vous). Il n'est pas adapté à une communication régulière sur une grande base.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">La notification push app</strong> suppose que l'utilisateur ait installé une application dédiée ET accordé l'autorisation de recevoir des notifications. Ces deux barrières réduisent mécaniquement la base atteignable. Le taux d'installation des applications de fidélité est en recul constant depuis 2022.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">La notification push navigateur</strong> est accessible depuis un site web, sans app. La fenêtre de permission du navigateur produit des taux d'opt-in bas (5 à 10 %), mais la base se constitue passivement à partir du trafic existant. Canal adapté aux médias, e-commerces et sites à fort trafic.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              <strong className="font-semibold">Le push wallet</strong> est le canal le moins connu et le plus performant. Apple Wallet et Google Wallet sont préinstallés sur tous les smartphones. Le client scanne un QR code en 30 secondes : la carte s'ajoute dans son wallet sans compte à créer ni app à télécharger. Les notifications push envoyées via ce canal s'affichent directement sur l'écran de verrouillage du téléphone, avec un taux d'ouverture de 50 à 55 % en moyenne.
            </p>

            <img
              src={canauxPushImage}
              alt="Comparatif des taux d’ouverture des canaux push en 2026 avec coût par canal et performance du push wallet"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Section 3 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Push marketing vs pull marketing : deux logiques complémentaires
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'erreur la plus fréquente est de traiter le push et le pull comme deux stratégies concurrentes. Ils répondent à des objectifs différents selon l'étape du cycle de vie client.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">pull marketing</strong> travaille en haut du funnel. Un article de blog bien référencé, une vidéo YouTube, une annonce Google sur une requête à forte intention : ce sont des outils qui attirent des personnes qui cherchent activement. Le pull est indispensable pour l'acquisition de nouvelles audiences qui ne vous connaissent pas encore.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">push marketing</strong> travaille en milieu et bas de funnel. Une fois qu'un prospect ou client a accepté de vous entendre (opt-in), le push est le levier le plus direct pour le faire agir : revenir après une absence, utiliser un avantage avant expiration, découvrir une nouveauté au moment où vous la lancez.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La formule la plus utile : le pull remplit votre base, le push la fait vivre.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un exemple d'articulation concret : un article de blog bien positionné (pull) amène un visiteur sur votre site. Ce visiteur scanne le QR code de votre carte wallet (opt-in). Vous lui envoyez un message de bienvenue le lendemain (push). Trois semaines plus tard, vous lui envoyez un rappel de son solde de points (push). Il revient. Aucun de ces deux canaux n'aurait produit ce résultat seul.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Pour choisir entre push et pull selon votre situation et votre étape de développement, notre guide sur <Link to="/push-vs-pull-marketing" className="text-[#0c97a9] hover:underline">push vs pull marketing</Link> détaille les critères de décision.
            </p>

            {/* Section 4 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Quand le push marketing fonctionne, et quand il échoue
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">push marketing</strong> est un outil direct. Sa performance dépend entièrement de la pertinence du message au moment de la réception.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Cinq conditions produisent des résultats mesurables. Le message arrive au bon moment : déclenché par un comportement (absence de 30 jours, solde de points qui approche d'un seuil, anniversaire). Le message est pertinent pour le destinataire : une donnée connue de lui est utilisée (prénom, historique d'achat, fréquence de visite). Le canal est adapté à l'urgence et au profil de la cible. La fréquence est maîtrisée : 1 à 2 messages directs par semaine maximum. Le message apporte une valeur immédiate et ne demande qu'une seule action.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les échecs d'une stratégie push se concentrent sur 4 causes récurrentes. L'envoi de masse non segmenté produit un taux de désabonnement élevé et un taux de clic proche de zéro. La fréquence excessive crée ce que les professionnels appellent la "push fatigue" : le destinataire désactive les notifications ou se désinscrit. Un message générique sans contexte n'est pas perçu différemment d'une publicité. Une communication sans valeur immédiate est ignorée en moins de 3 secondes.
            </p>

            {/* Section 5 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Exemples concrets de push marketing qui fonctionnent
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les meilleurs exemples ne viennent pas des grandes marques avec des équipes dédiées. Ils viennent de petites structures qui ont compris le mécanisme.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un café avec 300 membres wallet envoie chaque jeudi à 10h un message court : "Votre 5e café vous attend ce week-end." Résultat : une hausse de 18 % du trafic du vendredi et samedi sur les semaines d'envoi, mesurée sur 3 mois. Pas de remise, pas de contenu complexe. Un rappel au bon moment sur le bon canal.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un coach en formation envoie un email push 48 heures avant la fermeture d'une session : "Il reste 3 places. La session commence lundi." Sur une base de 600 abonnés, 60 à 70 % des inscriptions se concentrent sur cette fenêtre de 24 heures. Le push a créé l'urgence que le contenu seul n'avait pas suffi à déclencher.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un salon de coiffure envoie un SMS ciblé aux clients absents depuis 6 semaines : "Ça fait un moment. Votre créneau habituel du samedi est disponible cette semaine." Taux de retour : 3 fois supérieur à celui d'un email générique envoyé à toute la base.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Pour comprendre pourquoi les notifications push obtiennent des taux d'ouverture supérieurs à 50 %, notre guide sur <Link to="/notifications-push-taux-ouverture" className="text-[#0c97a9] hover:underline">les notifications push et leur taux d'ouverture</Link> entre dans le détail des formats et des mécanismes.
            </p>

            {/* Section 6 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Comment mesurer l'efficacité d'une stratégie push marketing
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un <strong className="font-semibold">programme push marketing</strong> sans mesure est un programme aveugle. Cinq indicateurs suffisent pour évaluer ce qui fonctionne.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse rounded-xl overflow-hidden text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold">KPI</th>
                    <th className="px-4 py-3 text-left font-semibold">Ce qu'il mesure</th>
                    <th className="px-4 py-3 text-left font-semibold">Seuil de référence (push wallet)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Taux d'ouverture</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">% de destinataires ayant ouvert le message</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">50–55 %</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Taux de clic (CTR)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">% ayant cliqué sur l'action proposée</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">10–15 %</td>
                  </tr>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Taux de conversion post-push</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">% ayant réalisé l'action voulue (achat, visite)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">5–10 %</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Taux de désabonnement</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Signal d'alerte sur fréquence ou pertinence</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">&lt; 0.1 %</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Revenu incrémental</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">CA directement généré par la campagne</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Variable selon secteur</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le taux d'ouverture donne une indication de la pertinence perçue du message. Si ce taux chute sous 30 % sur un canal push wallet, c'est un signal que la fréquence est trop élevée ou que les messages ne sont pas assez personnalisés.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le taux de désabonnement est l'indicateur d'alerte le plus rapide. Sur un canal direct (push wallet, SMS), un taux supérieur à 0.3 % par campagne indique un problème de pertinence ou de fréquence à corriger immédiatement.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              Pour aller plus loin sur la personnalisation des campagnes et maximiser l'impact de chaque message envoyé, notre guide sur le <Link to="/marketing-one-to-one" className="text-[#0c97a9] hover:underline">marketing one-to-one</Link> explique comment segmenter et cibler pour construire des messages qui semblent écrits pour une personne en particulier.
            </p>

            <img
              src={kpiPushImage}
              alt="Dashboard des 5 KPI du push marketing avec métriques de performance taux d’ouverture clic et engagement"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Section 7 - 2 colonnes desktop */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Le push wallet : le canal push le plus direct de 2026
            </h2>

            <div className="grid lg:grid-cols-2 gap-8 items-start mb-8">
              <div>
                <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
                  Le <strong className="font-semibold">push wallet</strong> représente l'évolution naturelle du push marketing à l'ère du tout-mobile. Il combine ce que les autres canaux n'ont pas réussi à réunir : une friction d'opt-in proche de zéro, un taux d'ouverture exceptionnel, et une indépendance totale vis-à-vis de tout algorithme ou intermédiaire.
                </p>
                <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
                  En France, 37,8 millions de cartes sont actives dans Apple Wallet et Google Wallet en 2026. Ces wallets ne sont plus réservés aux cartes bancaires : cartes de fidélité, coupons, billets d'événements, cartes cadeau, et cartes membres y coexistent. Le consommateur ouvre son wallet plusieurs fois par jour.
                </p>
                <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
                  Une notification envoyée via ce canal s'affiche sur l'écran de verrouillage du téléphone avec le nom et l'icône de la carte associée. Ce contexte de confiance, combiné à l'absence de boîte mail surchargée, explique le taux d'ouverture de 50 à 55 %. À titre de comparaison : l'email produit 23 à 30 % d'ouverture en moyenne, le push app entre 5 et 10 %.
                </p>
                <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
                  La mise en place est rapide : une carte wallet, un QR code, et un premier message planifié. Un commerce local peut déployer son programme en moins d'une journée.
                </p>
                <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
                  Pour comprendre comment les <strong className="font-semibold">notifications push via wallet</strong> fonctionnent techniquement et dans quels cas les déployer, notre guide sur le <Link to="/notifications-push-wallet" className="text-[#0c97a9] hover:underline">push wallet</Link> entre dans le détail.
                </p>
              </div>
              <img
                src={walletEcranImage}
                alt="Exemple de notification push wallet sur écran verrouillé avec message de fidélité visible sans application"
                loading="lazy"
                className="rounded-2xl w-full h-auto"
                style={imageStyle}
              />
            </div>

            {/* Conclusion */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4 mt-12">
              Le <strong className="font-semibold">push marketing</strong> n'est pas une technique agressive. C'est une initiative de contact qui, bien construite, apporte de la valeur au bon moment à la bonne personne. La différence entre une campagne push qui irrite et une campagne qui engage tient à deux paramètres : la pertinence du message et la maîtrise de la fréquence. En 2026, le canal push le plus direct, le moins coûteux et le plus performant reste la notification wallet. Sans app, sans algorithme, sans intermédiaire. Unotif permet de créer et d'envoyer vos premiers messages push wallet en moins d'une journée.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PushMarketing;
